import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { animations, colors } from '../../../core/styles/theme/default';
import { rem } from 'polished';
import Label from './parts/Label';
import FieldError from './parts/FieldError';
import Description from './parts/Description';
import { InputType } from './Input';
import i18n from '../measure-i18n';
import Uppy, { Meta, UploadResult, UppyFile } from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import DragDrop from '@uppy/drag-drop';
import ProgressBar from '@uppy/progress-bar';
import German from '@uppy/locales/lib/de_DE';
import French from '@uppy/locales/lib/fr_FR';
import Italian from '@uppy/locales/lib/it_IT';

import '@uppy/core/dist/style.css';
import '@uppy/drag-drop/dist/style.css';
import '@uppy/progress-bar/dist/style.css';

const StyledUploadInput = styled.div`
  .uploadInputDragDrop button {
    font-size: ${rem(16)};
    border: ${rem(1)} dashed currentColor;
    border-radius: ${rem(2)};

    &:focus {
      outline: ${rem(2)} solid ${colors.blue800};
      outline-offset: ${rem(-1)};
      box-shadow: none;
    }

    .uppy-DragDrop-inner {
      padding: ${rem(20)};
    }

    .uppy-DragDrop-label,
    .uppy-DragDrop-note {
      font-size: ${rem(16)};
    }
  }

  .uploadInputProgressBar {
    .uppy-ProgressBar {
      margin-top: ${rem(-3)};
    }
    .uppy-ProgressBar-inner {
      box-shadow: none;
    }
  }

  &[data-error='true'] {
    .uploadInputDragDrop button {
      border-color: ${colors.error};
    }
  }
`;

const StyledLogoPreviewWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(16)};
  margin-top: ${rem(16)};
`;

const StyledLogoPreview = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-direction: column;
  max-width: 100%;

  div {
    max-width: 100%;
    width: ${rem(280)};
    height: ${rem(152)};
    background-color: ${colors.grey100};
    border-radius: ${rem(2)};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  button {
    position: absolute;
    top: 0;
    right: 0;
    background: ${colors.blue900};
    color: ${colors.white};
    border-radius: 50%;
    padding: 0;
    width: ${rem(18)};
    height: ${rem(18)};
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    opacity: 0.9;
    transform: translate(50%, -50%) rotate(45deg);
    transition: opacity ${animations.fast};

    &::before {
      content: '+';
      font-size: ${rem(18)};
      line-height: 0;
    }

    &:focus {
      outline: ${rem(2)} solid ${colors.blue800};
      outline-offset: ${rem(-1)};
    }

    &:focus,
    &:hover {
      opacity: 1;
    }
  }
`;

type UploadResponse = {
  result: {
    fileId: string;
  };
};

const UploadInput = ({
  allowedFileTypes,
  maxNumberOfFiles,
  maxFileSize,
  onChange,
  isDisabled,
  api,
  formId,
  fieldId,
  locale,
  error,
  suffix,
}: UploadInputType) => {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uppyFiles, setUppyFiles] = useState<
    UppyFile<Meta, Record<string, never>>[]
  >([]);
  const uppyRef = useRef<Uppy | null>(null);

  useEffect(() => {
    const uppy = new Uppy({
      id: `uploadInput_${fieldId}_${locale}`,
      locale: locale === 'it' ? Italian : locale === 'fr' ? French : German,
      restrictions: {
        maxNumberOfFiles,
        maxFileSize,
        allowedFileTypes,
      },
      autoProceed: true,
    });

    // Save Uppy instance to useRef to persist across renders
    uppyRef.current = uppy;

    // Event Handlers
    const handleFileAdded = (file: UppyFile<Meta, Record<string, never>>) => {
      uppy.setFileMeta(file.id, { formId });
    };

    const handleUpload = () => {
      setUploadError(null);
    };

    const handleComplete = (
      result: UploadResult<Meta, Record<string, never>>,
    ) => {
      setUppyFiles(uppy.getFiles());

      if (result.failed?.length) {
        uppy.emit('info-visible');
      }
    };

    const handleInfoVisible = () => {
      const message = uppy.getState().info?.[0]?.message;

      if (message) {
        setUploadError(message);
      }
    };

    // Setup Uppy plugins
    uppy.use(XHRUpload, { endpoint: api });
    uppy.use(DragDrop, {
      target: `#uploadInputDragDrop_${fieldId}_${locale}`,
      locale: {
        pluralize: (n) => n,
        strings: {
          dropHereOr: i18n.dropHereOr[locale],
          browse: i18n.browse[locale],
        },
      },
    });
    uppy.use(ProgressBar, {
      target: `#uploadInputProgressBar_${fieldId}_${locale}`,
      hideAfterFinish: true,
    });

    // Register event listeners
    uppy.on('file-added', handleFileAdded);
    uppy.on('upload', handleUpload);
    uppy.on('complete', handleComplete);
    uppy.on('info-visible', handleInfoVisible);

    // Cleanup on component unmount
    return () => {
      uppy.off('file-added', handleFileAdded);
      uppy.off('upload', handleUpload);
      uppy.off('complete', handleComplete);
      uppy.off('info-visible', handleInfoVisible);
      uppy.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const files = uppyFiles.map((file) => ({
      name: file.name ?? '',
      fileId: (file.response?.body as unknown as UploadResponse)?.result
        ?.fileId,
    }));

    onChange(files.length ? files : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uppyFiles]);

  return (
    <>
      <StyledUploadInput
        style={{
          ...(uppyFiles.length === maxNumberOfFiles && { display: 'none' }),
          ...(isDisabled && { pointerEvents: 'none', opacity: '0.3' }),
        }}
        data-error={!!error}
      >
        <div
          id={`uploadInputDragDrop_${fieldId}_${locale}`}
          className="uploadInputDragDrop"
        />
        <div
          id={`uploadInputProgressBar_${fieldId}_${locale}`}
          className="uploadInputProgressBar"
        />
        {suffix ? (
          <Description small bottom>
            {suffix}
          </Description>
        ) : null}
      </StyledUploadInput>
      {uploadError && <FieldError text={uploadError} />}
      {uppyFiles.length ? (
        <StyledLogoPreviewWrapper>
          {uppyFiles.map((file) => (
            <StyledLogoPreview key={file.id}>
              <div>
                <img
                  src={`${api}?fileId=${
                    (file.response?.body as unknown as UploadResponse)?.result
                      ?.fileId
                  }`}
                  alt={file.name}
                />
              </div>
              {file.name}
              <button
                title={i18n.remove[locale]}
                onClick={() => {
                  const uppy = uppyRef.current;
                  if (!uppy) return;

                  uppy.removeFile(file.id);
                  setUppyFiles(uppy.getFiles());
                }}
              />
            </StyledLogoPreview>
          ))}
        </StyledLogoPreviewWrapper>
      ) : null}
    </>
  );
};

const InputFile = ({
  id,
  label,
  locale,
  value,
  onChange,
  error,
  isRequired,
  isDisabled,
  prefix,
  suffix,
  errorRenderer,
  maxFileSize,
  maxNumberOfFiles,
  allowedFileTypes,
  api,
  formId,
}: InputFileType) => (
  <div>
    {label ? (
      <Label as={'div'} isRequired={isRequired}>
        {label}
      </Label>
    ) : null}
    <div>
      {prefix ? <Description top>{prefix}</Description> : null}
      <UploadInput
        key={id}
        value={value}
        allowedFileTypes={allowedFileTypes}
        maxFileSize={maxFileSize}
        maxNumberOfFiles={maxNumberOfFiles}
        onChange={(obj: InputFileType['value']) => {
          onChange(obj);
        }}
        isDisabled={isDisabled}
        api={api}
        formId={formId}
        fieldId={id}
        locale={locale}
        error={error}
        suffix={suffix}
      />
    </div>
    {error ? (
      errorRenderer ? (
        errorRenderer(error)
      ) : (
        <FieldError text={i18n.requiredField[locale]} />
      )
    ) : null}
  </div>
);

type InputFileType = Omit<InputType, 'value' | 'onChange'> & {
  maxFileSize: number;
  maxNumberOfFiles: number;
  allowedFileTypes: string[];
  api: string;
  formId: string;
  fieldId: string;
  value?: {
    name: string;
    fileId: string;
  }[];
  onChange: (value?: InputFileType['value']) => void;
};

type UploadInputType = {
  allowedFileTypes: string[];
  maxFileSize: number;
  maxNumberOfFiles: number;
  onChange: InputFileType['onChange'];
  isDisabled: boolean;
  value?: InputFileType['value'];
  api: string;
  formId: string;
  fieldId: string;
  locale: string;
  error: string;
  suffix?: string;
};

export const isValid = (value: any, config: any) => {
  if (config.isRequired && (value === '' || typeof value === 'undefined'))
    return false;
  return true;
};

export default InputFile;
